import * as Accordion from "@radix-ui/react-accordion";
import React from "react";
import SVG from "react-inlinesvg";
import { Tooltip } from "react-tooltip";
import { c, t } from "ttag";

import {
    CompareFeatureBlock,
    TsicommonCmsCompareFeatureOrNull,
} from "@reactivated";

import CaretDownIcon from "../../../img/icons/caret-down.svg";
import QuestionMarkCircleIcon from "../../../img/icons/question-mark-circle-filled.svg";
import { useCurrentBreakpoint } from "../../utils/hooks";
import { CompareFeatureRow } from "../CompareFeatureRow";
import { RichText } from "../RichText";

import styles from "./index.module.scss";

interface ICompareFeaturesProps {
    features: TsicommonCmsCompareFeatureOrNull[];
    compareTileFeatures?: CompareFeatureBlock[][];
}

const hasContent = (html: string | null | undefined): boolean => {
    if (!html) return false;
    return html.replace(/<[^>]*>/g, "").trim().length > 0;
};

export const CompareFeatures = (props: ICompareFeaturesProps) => {
    const currentBreakpoint = useCurrentBreakpoint();
    const tooltipPlace = currentBreakpoint.belowLaptop ? "top" : "right";

    if (!props.compareTileFeatures) {
        return null;
    }
    return (
        <div className={styles.compareFeaturesContainer}>
            <Accordion.Root
                type="multiple"
                defaultValue={props.features.map((ftr) => ftr?.title || "")}
            >
                {props.features.map((ftr, index) => {
                    if (!ftr) {
                        return null;
                    }

                    return (
                        <Accordion.Item
                            key={ftr.title}
                            value={ftr.title || ""}
                            className={styles.accordionItem}
                        >
                            <Accordion.Trigger>
                                <div className={styles.accordionTrigger}>
                                    <SVG
                                        src={CaretDownIcon}
                                        className={`arrow ${styles.accordionArrowIcon}`}
                                    />
                                    <div
                                        className={styles.accordionTriggerTitle}
                                    >
                                        {ftr.title || ""}
                                    </div>
                                    {hasContent(ftr.tooltip) && (
                                        <sup>
                                            <span
                                                data-tooltip-id={`compare-feature-tooltip--${index}`}
                                                tabIndex={0}
                                            >
                                                <SVG
                                                    className={
                                                        styles.tooltipIcon
                                                    }
                                                    src={QuestionMarkCircleIcon}
                                                    aria-hidden="true"
                                                />
                                                <span className="ada-screenreader-only">
                                                    {ftr.title
                                                        ? c("CompareFeatures")
                                                              .t`More information about ${ftr.title}`
                                                        : t`More information`}
                                                </span>
                                            </span>
                                            <Tooltip
                                                className="tooltip"
                                                globalCloseEvents={{
                                                    escape: true,
                                                    scroll: true,
                                                }}
                                                id={`compare-feature-tooltip--${index}`}
                                                place={tooltipPlace}
                                            >
                                                <RichText html={ftr.tooltip!} />
                                            </Tooltip>
                                        </sup>
                                    )}
                                </div>
                            </Accordion.Trigger>
                            <Accordion.Content>
                                <CompareFeatureRow
                                    feature={ftr}
                                    content={props.compareTileFeatures?.map(
                                        (ctf) =>
                                            ctf.find(
                                                (item) =>
                                                    item?.feature?.title ===
                                                    ftr.title,
                                            ) || null,
                                    )}
                                />
                            </Accordion.Content>
                        </Accordion.Item>
                    );
                })}
            </Accordion.Root>
        </div>
    );
};
